import React, { useEffect, useState } from 'react';
import Modal from '../../../../shared/components/core-ui/modal/modal.component';
import Button, { ButtonType } from '../../../../shared/components/core-ui/button/button.component';
import { LeadConfigModel } from '../../../../shared/models/lead.model';
import { LeadUiModel } from '../../../../shared/ui-models/lead.ui-model';

interface StatusModalProps {
  show: boolean;
  lead: LeadUiModel;
  config: LeadConfigModel;
  onClose: () => void;
  onSubmit: (payload: Record<string, unknown>) => void;
}

const titleCase = (value: string): string =>
  value.replace(/_/g, ' ').replace(/^./, (c) => c.toUpperCase());

export const StatusModal: React.FC<StatusModalProps> = ({
  show,
  lead,
  config,
  onClose,
  onSubmit
}) => {
  const [status, setStatus] = useState('pending');
  const [resolutionType, setResolutionType] = useState('');
  const [reason, setReason] = useState('');
  const [note, setNote] = useState('');

  useEffect(() => {
    setStatus('pending');
    setResolutionType('');
    setReason('');
    setNote('');
  }, [show]);

  /**
   * Only the selectable list. The full `resolutionTypesByVertical` includes
   * values the service sets itself — `unreachable`, set when the attempt quota
   * runs out — and sending one back is rejected outright. Offering it would be
   * a picker whose options cannot all be picked.
   */
  const resolutions = config.selectableResolutionTypesByVertical[lead.vertical] ?? [];

  // Which reason field this resolution needs, straight from the config so the
  // rule lives in one place rather than being re-encoded here.
  const reasonField = config.requiredReasonByResolution[resolutionType];
  const reasonOptions =
    reasonField === 'notQualifiedReason'
      ? config.notQualifiedReasons
      : reasonField === 'notInterestedReason'
      ? config.notInterestedReasons
      : [];

  const isResolving = status === 'resolved';
  const needsPendingReason = status === 'pending';
  const canSubmit =
    (!isResolving || (resolutionType.length > 0 && (!reasonField || reason.length > 0))) &&
    (!needsPendingReason || reason.length > 0);

  const buildPayload = (): Record<string, unknown> => {
    const payload: Record<string, unknown> = { status };
    if (isResolving) {
      payload.resolutionType = resolutionType;
      if (reasonField) payload[reasonField] = reason;
      if (note.trim()) payload.resolutionNote = note.trim();
    }
    if (needsPendingReason) payload.pendingReason = reason;
    return payload;
  };

  return (
    <Modal
      config={{ showModal: show, fullScreen: false, maxWidth: 'sm', showClose: true, autoHeight: true }}
      eventHandlers={{ closeModalHandler: onClose }}
    >
      <div className="flex flex-col gap-4 px-6 py-4">
        <h2 className="text-lg font-semibold">Change status</h2>

        <div>
          <label className="mb-1 block text-sm text-gray-600">Status</label>
          <select
            className="w-full rounded border border-gray-300 px-3 py-2"
            value={status}
            onChange={(event) => {
              setStatus(event.target.value);
              setReason('');
              setResolutionType('');
            }}
          >
            {config.statuses
              // Set by the service, never by a person: `duplicate` has its own
              // action because it needs the id of the lead it duplicates.
              .filter((option) => option !== 'duplicate' && option !== 'not_reachable')
              .map((option) => (
                <option key={option} value={option}>
                  {titleCase(option)}
                </option>
              ))}
          </select>
        </div>

        {isResolving ? (
          <div>
            <label className="mb-1 block text-sm text-gray-600">Resolution</label>
            <select
              className="w-full rounded border border-gray-300 px-3 py-2"
              value={resolutionType}
              onChange={(event) => {
                setResolutionType(event.target.value);
                setReason('');
              }}
            >
              <option value="">Select a resolution</option>
              {resolutions.map((option) => (
                <option key={option} value={option}>
                  {titleCase(option)}
                </option>
              ))}
            </select>
          </div>
        ) : null}

        {(reasonField && isResolving) || needsPendingReason ? (
          <div>
            <label className="mb-1 block text-sm text-gray-600">
              {needsPendingReason ? 'Why is it pending?' : titleCase(reasonField ?? 'Reason')}
            </label>
            <select
              className="w-full rounded border border-gray-300 px-3 py-2"
              value={reason}
              onChange={(event) => setReason(event.target.value)}
            >
              <option value="">Select a reason</option>
              {(needsPendingReason ? config.pendingReasons : reasonOptions).map((option) => (
                <option key={option} value={option}>
                  {titleCase(option)}
                </option>
              ))}
            </select>
          </div>
        ) : null}

        {isResolving ? (
          <div>
            <label className="mb-1 block text-sm text-gray-600">Note (optional)</label>
            <textarea
              className="w-full rounded border border-gray-300 px-3 py-2"
              rows={3}
              value={note}
              onChange={(event) => setNote(event.target.value)}
            />
          </div>
        ) : null}
      </div>

      <div className="flex flex-col gap-3 px-6 pb-6 sm:flex-row sm:justify-end">
        <Button type={ButtonType.gray} onClick={onClose}>
          Cancel
        </Button>
        <Button
          type={ButtonType.primary}
          disabled={!canSubmit}
          tracking={{ event: 'record_saved', entity: 'lead', action: 'update', outcome: 'success' }}
          onClick={() => onSubmit(buildPayload())}
        >
          Save
        </Button>
      </div>
    </Modal>
  );
};
